import { GcpBox, GcpTypography } from '@back-office/ui';
import {
  Button,
  Dialog,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import React, { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import useViewModel from './viewmodel';

const useStyles = makeStyles((theme) => ({
  icon: {
    fill: theme.palette.common.mineShaft,
  },
  fileProcessingModal: {
    width: '326px',
  },
  button: {
    height: '32px',
    boxShadow: 'none',
    width: '50%',
    '&:hover': {
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: '#F5F5F5 !important',
      color: '#00000040 !important',
      border: '1px solid #00000040 !important',
    },
  },
  cancel: {
    backgroundColor: 'white',
    '&:hover': {
      backgroundColor: 'white',
    },
  },

  headerSection: {
    padding: '20px 20px',
    textAlign: 'center',
    '& > h3': {
      color: '#333',
      lineHeight: '32px',
      fontSize: '24px',
    },
  },
  datePicker: {
    width: '100%',
    border: '1px solid #BDBDBD',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#BDBDBD',
    '& > .MuiButton-label': {
      gap: `${theme.spacing(1)}px`,
    },
    '&:hover': {
      border: `1px solid ${theme.palette.common.ceriseRed}`,
      color: theme.palette.common.ceriseRed,
      backgroundColor: 'unset',
      '& > span > svg': {
        color: theme.palette.common.ceriseRed,
      },
      '& > span > div > svg > path': {
        fill: theme.palette.common.ceriseRed,
      },
    },
  },
  textField: {
    width: '100%',
  },
  checked: {
    color: '#D82662  !important',
  },
  error: {
    color: `${theme.palette.common.ceriseRed} !important`,
  },
}));

export interface Store {
  option: string;
  openCalendar: boolean;
  handleOnChangeValue: (value: string) => void;
  handleOnCancel: (event: MouseEvent<HTMLOrSVGElement>) => void;
  handleOnUpload: () => void;
  handleClose: (event: MouseEvent<HTMLOrSVGElement>, reason: string) => void;
}

interface UploadOptions {
  scheduleDate: string;
  isProcessingNow: boolean;
}

export interface FileProcessingModalProps {
  open: boolean;
  handleOnClose: () => void;
  handleOnConfirm: (options: UploadOptions) => void;
}

const FileProcessingModal: FC<FileProcessingModalProps> = observer((props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { open, handleOnConfirm } = props;
  const {
    state,
    selectedValue,
    handleRadioChange,
    selectedDate,
    handleOnChangeDate,
    isValidDate,
  } = useViewModel(props);

  return (
    <Dialog
      aria-labelledby="export-to-csv-dialog"
      classes={{ paper: classes.fileProcessingModal }}
      open={open}
      onClose={state.handleClose}
    >
      <GcpBox
        alignItems="center"
        className={classes.headerSection}
        display="flex"
        justifyContent="center"
      >
        <GcpTypography variant="h3">
          {t('payment_file.upload_logs.modal.title')}
        </GcpTypography>
      </GcpBox>
      <GcpBox ml={3} mr={3}>
        <GcpTypography className="description">
          {t('payment_file.upload_logs.modal.description')}
        </GcpTypography>
      </GcpBox>
      <GcpBox ml={3} mr={3}>
        <FormControl>
          <RadioGroup
            color="primary"
            defaultValue="process_now"
            name="radio-buttons-group"
            value={selectedValue}
            onChange={handleRadioChange}
          >
            <FormControlLabel
              control={<Radio classes={{ checked: classes.checked }} />}
              label={t('payment_file.upload_logs.modal.processing')}
              value="process_now"
            />
            <FormControlLabel
              control={<Radio classes={{ checked: classes.checked }} />}
              label={t('payment_file.upload_logs.modal.schedule')}
              value="schedule"
            />
          </RadioGroup>
        </FormControl>
        {selectedValue === 'schedule' && (
          <GcpBox display="flex" flexDirection="column" gap={1}>
            <TextField
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: { max: '9999-12-31' },
              }}
              className={classes.textField}
              defaultValue={selectedDate}
              id="date"
              size="small"
              type="date"
              variant="outlined"
              onChange={handleOnChangeDate}
            />
            {selectedDate !== null && isValidDate(selectedDate) < 0 && (
              <GcpTypography className={classes.error}>
                {t('payment_file.upload_logs.modal.previous_date_error')}
              </GcpTypography>
            )}
          </GcpBox>
        )}
      </GcpBox>
      <GcpBox
        display="flex"
        gap={2}
        justifyContent="center"
        mb={2}
        ml={3}
        mr={3}
        mt={2}
      >
        <Button
          className={clsx(classes.button, classes.cancel)}
          disabled={false}
          size="large"
          type="button"
          variant="outlined"
          onClick={state.handleOnCancel}
        >
          {t('dialog.cancel')}
        </Button>
        <Button
          className={classes.button}
          color="primary"
          disabled={
            selectedValue === 'schedule' && isValidDate(selectedDate) < 1
          }
          size="large"
          type="button"
          variant="contained"
          onClick={() => {
            handleOnConfirm({
              scheduleDate: selectedDate
                ? new Date(selectedDate).toISOString()
                : new Date().toISOString(),
              isProcessingNow: selectedValue === 'process_now',
            });
          }}
        >
          {t('dialog.confirm')}
        </Button>
      </GcpBox>
    </Dialog>
  );
});

export default FileProcessingModal;
